import React, { useState, useEffect, useContext, useMemo } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Modal from "bootstrap/js/dist/modal";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import Select from "react-select";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { CreditCard, Users } from "lucide-react";
import styled from "styled-components";
import { Lock } from "lucide-react";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";

const route = all_routes;

const UserManagementLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading user management data...</span>
  </div>
);

const TableContainer = styled.div`
  max-height: calc(5 * 70px);
  min-height: calc(5 * 70px);
  overflow-y: auto;
`;
interface User {
  tenantUserId: number;
  userId: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role: string;
  role_id?: string | number | null;
  status: "Active" | "Inactive";
  created_date: string;
}

const MySwal = withReactContent(Swal);

const UserManagement = () => {
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);
  const userRole = useTenantRole(selectedTenantId || 0);

  const [users, setUsers] = useState<User[]>([]);
  const rowsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isSavingNew, setIsSavingNew] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [usedLicenses, setUsedLicenses] = useState(0);
  const [maxLicenses, setMaxLicenses] = useState(0);
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  const startIdx = (currentPage - 1) * rowsPerPage;
  const paginatedUsers = users;

  const [originalEditData, setOriginalEditData] = useState<{
    firstName: string;
    lastName: string;
    roleId: string | number;
    isActive: boolean;
  } | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [newStatus, setNewStatus] = useState<"Active" | "Inactive">("Active");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [userExistsLoading, setUserExistsLoading] = useState(false);

  const [editTenantUserId, setEditTenantUserId] = useState<number | null>(null);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [editStatus, setEditStatus] = useState<"Active" | "Inactive">("Active");
  const [editPassword, setEditPassword] = useState("");
  const [editPasswordError, setEditPasswordError] = useState("");
  const [licenseStats, setLicenseStats] = useState<{
    total_tenant_users: number;
    maximum_allowed_users_in_subscription: number;
  } | null>(null);

  const [roleOptions, setRoleOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [isModalOpening, setIsModalOpening] = useState(false);

  const validatePassword = (pwd: string) =>
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/.test(
      pwd
    );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = paginatedUsers
        .filter((item) => item.tenantUserId)
        .map((item) => item.tenantUserId);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const currentUserRole = useMemo(() => {
    if (!auth?.tenants || !selectedTenantId) return null;
    const currentTenant = auth.tenants.find(
      (t: any) => String(t.tenant_id) === String(selectedTenantId)
    );
    return currentTenant?.role_key || null;
  }, [auth?.tenants, selectedTenantId]);

  const handleSelectItem = (tenantUserId: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(tenantUserId)
        ? prev.filter((id) => id !== tenantUserId)
        : [...prev, tenantUserId];

      const validCurrentData = paginatedUsers.filter(
        (item) => item.tenantUserId
      );
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  const getPageNumbers = () => {
    const maxButtons = 3;
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const openEditModal = (user: User) => {
    setEditTenantUserId(user.tenantUserId);
    setEditUserId(user.userId);

    const firstName = user.first_name || "";
    const lastName = user.last_name || "";
    setEditFirstName(firstName);
    setEditLastName(lastName);

    setEditEmail(user.email);
    const role = roleOptions.find((r) => r.label === user.role) || null;
    setEditRole(role);
    setEditStatus(user.status);

    setOriginalEditData({
      firstName: firstName,
      lastName: lastName,
      roleId: role?.value ?? "",
      isActive: user.status === "Active",
    });

    setEditPassword("");
    setEditPasswordError("");
  };

  const fetchUsers = async (page: number) => {
    if (!selectedTenantId) return;
    setIsLoading(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}&page=${page}&page_size=${rowsPerPage}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(
        (data.results || []).map((item: any) => ({
          tenantUserId: item.tenant_user_id,
          userId: item.user_id,
          name: `${item.user_first_name || ""} ${item.user_last_name || ""
            }`.trim(),
          first_name: item.user_first_name || "",
          last_name: item.user_last_name || "",
          email: item.email || "-",
          role: item.role_display_name || "-",
          status: item.is_active ? "Active" : "Inactive",
          created_date: item.created_datetime || "-",
        }))
      );
      setTotalCount(data.count);
      setTotalPages(Math.ceil(data.count / rowsPerPage));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  const loadLicenseStats = async () => {
    if (!selectedTenantId) return;
    setIsStatsLoading(true);
    const authHeader = localStorage.getItem("authCred") || "";
    try {
      const res = await fetch(
        `${API_BASE_URL}/lookup/tenant-user-stats/${selectedTenantId}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch stats");
      const data = await res.json();
      setUsedLicenses(data.total_tenant_users);
      setMaxLicenses(data.maximum_allowed_users_in_subscription);
    } catch (err) {
      console.error(err);
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    loadLicenseStats();
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    (async () => {
      try {
        const authHeader = localStorage.getItem("authCred") || "";
        const res = await fetch(
          `${API_BASE_URL}/lookup/tenant-user-stats/${selectedTenantId}`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch license stats");
        const data = await res.json();
        setLicenseStats(data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [selectedTenantId]);

  useEffect(() => {
    (async () => {
      try {
        const authHeader = localStorage.getItem("authCred") || "";
        const res = await fetch(`${API_BASE_URL}/role/`, {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error("Failed to fetch roles");
        const { results } = await res.json();
        setRoleOptions(
          (results || [])
            .filter((r: any) =>
              ["CLIENTADMIN", "SALESUSER"].includes(r.role_key)
            )
            .map((r: any) => ({ value: r.role_id, label: r.role_display_name }))
        );
      } catch {
        setRoleOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [selectedTenantId, currentPage]);

  useEffect(() => {
    const resetScroll = () => {
      const hasVisibleModals =
        document.querySelectorAll(".modal.show").length > 0;
      if (!hasVisibleModals) {
        document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.documentElement.style.overflow = "";
      }
    };
    resetScroll();
    return () => {
      resetScroll();
    };
  }, []);

  useEffect(() => {
    const checkModalStates = () => {
      if (isModalOpening) {
        return;
      }

      const hasModalBackdrop =
        document.querySelectorAll(".modal-backdrop").length > 0;
      const hasModalOpen = document.body.classList.contains("modal-open");
      const hasOverflowHidden = document.body.style.overflow === "hidden";
      const hasVisibleModals =
        document.querySelectorAll(".modal.show").length > 0;

      if (
        (hasModalBackdrop || hasModalOpen || hasOverflowHidden) &&
        !hasVisibleModals
      ) {
        document
          .querySelectorAll(".modal-backdrop")
          .forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.documentElement.style.overflow = "";
      }
    };

    checkModalStates();
    const interval = setInterval(checkModalStates, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isModalOpening]);

  useEffect(() => {
    const handleModalButtonClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.closest('[data-bs-toggle="modal"]') ||
          target.closest("[data-bs-target]"))
      ) {
        setIsModalOpening(true);
        setTimeout(() => {
          setIsModalOpening(false);
        }, 2000);
      }
    };

    document.addEventListener("click", handleModalButtonClick);

    return () => {
      document.removeEventListener("click", handleModalButtonClick);
    };
  }, []);

  useEffect(() => {
    if (paginatedUsers.length > 0) {
      const validCurrentData = paginatedUsers.filter(
        (item) => item.tenantUserId
      );
      const allCurrentSelected = validCurrentData.every(
        (item) => item.tenantUserId && selectedItems.includes(item.tenantUserId)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [paginatedUsers, selectedItems]);

  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [currentPage]);

  const checkUserExists = async (email: string) => {
    if (!email) return;
    setUserExistsLoading(true);
    try {
      const authHeader = localStorage.getItem("authCred") || "";
      const res = await fetch(
        `${API_BASE_URL}/user/?email=${encodeURIComponent(email)}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data && data.results && data.results.length > 0) {
        setUserExists(true);
        setFirstName(data.results[0].first_name || "");
        setLastName(data.results[0].last_name || "");
      } else {
        setUserExists(false);
        setFirstName("");
        setLastName("");
      }
    } catch {
      setUserExists(false);
      setFirstName("");
      setLastName("");
    } finally {
      setUserExistsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newEmail || !newRole) {
      return MySwal.fire({
        icon: "error",
        title: "Please fill in all fields",
        showConfirmButton: true,
      });
    }
    if (!userExists && (!firstName || !lastName || !password)) {
      return MySwal.fire({
        icon: "error",
        title: "Please fill in all fields",
        showConfirmButton: true,
      });
    }
    if (!userExists && !validatePassword(password)) {
      return MySwal.fire({
        icon: "error",
        title: "Password must meet complexity requirements",
        showConfirmButton: true,
      });
    }
    setIsSavingNew(true);
    const authHeader = localStorage.getItem("authCred") || "";
    try {
      let user_id = null;
      let userActiveStatus = false;
      let isExistingUser = false;

      const checkEmailRes = await fetch(
        `${API_BASE_URL}/user/?email=${encodeURIComponent(newEmail)}`,
        {
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (checkEmailRes.ok) {
        const emailData = await checkEmailRes.json();
        if (emailData.results && emailData.results.length > 0) {
          const existingUser = emailData.results[0];
          user_id = existingUser.user_id;
          userActiveStatus = existingUser.is_active || false;
          isExistingUser = true;
          setUserExists(true);
        }
      }

      if (!userExists && !user_id) {
        const userRes = await fetch(`${API_BASE_URL}/user/`, {
          method: "POST",
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tenant_id: selectedTenantId,
            first_name: firstName,
            last_name: lastName,
            email: newEmail,
            password,
            is_active: userActiveStatus,
            created_by: auth?.user_id,
            updated_by: auth?.user_id,
          }),
        });
        if (!userRes.ok) {
          const err = await userRes.json();
          if (err.email?.[0]?.toLowerCase().includes("exists")) {
            setUserExists(true);
            return MySwal.fire({
              icon: "error",
              title: "Email already exists",
              showConfirmButton: true,
            });
          }
          throw new Error();
        }
        const userData = await userRes.json();
        user_id = userData.data?.user_id || userData.user_id;
      }

      const tenantRes = await fetch(`${API_BASE_URL}/tenant-user/`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${authHeader}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tenant_id: selectedTenantId,
          user_id,
          role_id: newRole.value,
          is_active: userActiveStatus,
          updated_by: auth?.user_id,
        }),
      });
      if (!tenantRes.ok) {
        const err = await tenantRes.json();
        if (
          err.error &&
          err.error
            .toLowerCase()
            .includes("already exists in this subscription")
        ) {
          await MySwal.fire({
            icon: "error",
            title: "This user already exists in this subscription.",
            showConfirmButton: true,
          });
          setIsSavingNew(false);
          return;
        }
        throw new Error();
      }

      if (isExistingUser) {
        await MySwal.fire({
          icon: "success",
          title: "User has been added",
          showConfirmButton: true,
        });
      } else {
        await MySwal.fire({
          icon: "success",
          title: "User has been added",
          text: "Please complete verification. Link has been sent to the user email inbox.",
          showConfirmButton: true,
        });
      }

      cleanupAddUserModal();

      setTimeout(() => {
        const hasBackdrop =
          document.querySelectorAll(".modal-backdrop").length > 0;
        const hasModalOpen = document.body.classList.contains("modal-open");
        if (hasBackdrop || hasModalOpen) {
          document
            .querySelectorAll(".modal-backdrop")
            .forEach((e) => e.remove());
          document.body.classList.remove("modal-open");
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";
        }
      }, 300);

      fetchUsers(currentPage);
      await loadLicenseStats();
    } catch {
      MySwal.fire({
        icon: "error",
        title: "Failed to add user. Please try again.",
        showConfirmButton: true,
      });
    } finally {
      setIsSavingNew(false);
    }
  };

  const handleEditSave = async () => {
    if (!originalEditData || editUserId == null || editTenantUserId == null) {
      return MySwal.fire({
        icon: "error",
        title: "Please fill in all fields",
        text: "All fields are required to save changes.",
        confirmButtonText: "OK",
      });
    }

    const roleChanged = editRole && editRole.value !== originalEditData.roleId;
    const statusChanged =
      (editStatus === "Active") !== originalEditData.isActive;

    const userPayload: Record<string, any> = {
      updated_by: auth?.user_id,
      tenant_id: selectedTenantId,
    };
    if (editFirstName !== originalEditData.firstName)
      userPayload.first_name = editFirstName;
    if (editLastName !== originalEditData.lastName)
      userPayload.last_name = editLastName;
    if (editPassword) userPayload.password = editPassword;

    if (
      statusChanged &&
      editStatus === "Active" &&
      !originalEditData.isActive
    ) {
      userPayload.is_active = true;
    }

    const userChanged = Object.keys(userPayload).length > 1;
    const tenantChanged = roleChanged || statusChanged;
    const onlyTenantChanged = !userChanged && tenantChanged;

    const tenantPayload: Record<string, any> = { tenant_id: selectedTenantId };
    if (roleChanged) tenantPayload.role_id = editRole!.value;
    if (statusChanged) tenantPayload.is_active = editStatus === "Active";

    if (!userChanged && !tenantChanged) {
      return MySwal.fire({
        icon: "info",
        title: "Nothing changed",
        text: "You haven't updated anything.",
        confirmButtonText: "OK",
      });
    }

    setIsSavingEdit(true);
    const authHeader = localStorage.getItem("authCred") || "";

    try {
      if (onlyTenantChanged) {
        await fetch(`${API_BASE_URL}/user/${editUserId}/`, {
          method: "PATCH",
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tenant_id: selectedTenantId,
            updated_by: auth?.user_id,
          }),
        });
      } else if (userChanged) {
        await fetch(`${API_BASE_URL}/user/${editUserId}/`, {
          method: "PATCH",
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userPayload),
        });
      }

      if (tenantChanged) {
        if (!onlyTenantChanged) {
          tenantPayload.updated_by = auth?.user_id;
        }
        await fetch(`${API_BASE_URL}/tenant-user/${editTenantUserId}/`, {
          method: "PATCH",
          headers: {
            Authorization: `Basic ${authHeader}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(tenantPayload),
        });
      }

      await MySwal.fire({
        icon: "success",
        title: "User updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanupModal("edit_user");

      setTimeout(() => {
        const hasBackdrop =
          document.querySelectorAll(".modal-backdrop").length > 0;
        const hasModalOpen = document.body.classList.contains("modal-open");
        if (hasBackdrop || hasModalOpen) {
          document
            .querySelectorAll(".modal-backdrop")
            .forEach((e) => e.remove());
          document.body.classList.remove("modal-open");
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";
        }
      }, 300);

      fetchUsers(currentPage);

      if (editUserId === auth?.user_id) {
        const updatedAuth = {
          ...auth,
          first_name: editFirstName,
          last_name: editLastName,

          tenants: auth.tenants.map((t: any) =>
            String(t.tenant_id) === String(selectedTenantId)
              ? { ...t, role_name: editRole!.label }
              : t
          ),

          role_name: editRole!.label,
        };
        setAuth(updatedAuth);
        localStorage.setItem("authResponse", JSON.stringify(updatedAuth));
        localStorage.setItem("authTimestamp", Date.now().toString());
      }
    } catch (err) {
      console.error("Save failed:", err);
      MySwal.fire({
        icon: "error",
        title: "Save failed",
        text: "Could not save changes. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteUser = async (tenantUserId: number) => {
    setIsDeleting(true);
    const authHeader = localStorage.getItem("authCred") || "";

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch(`${API_BASE_URL}/tenant-user/${tenantUserId}/`, {
        method: "DELETE",
        headers: { Authorization: `Basic ${authHeader}` },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error("Failed to delete user");

      cleanupDeleteModal();

      await MySwal.fire({
        icon: "success",
        title: "User deleted successfully",
        showConfirmButton: false,
        timer: 1500,
      });

      await fetchUsers(currentPage);
      await loadLicenseStats();
    } catch (err) {
      console.error(err);
      let errorMessage = "Could not delete user. Please try again.";

      if (err instanceof Error) {
        if (err.name === "AbortError") {
          errorMessage = "Delete operation timed out. Please try again.";
        } else if (err.message.includes("Failed to fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        }
      }

      await MySwal.fire({
        icon: "error",
        title: errorMessage,
        showConfirmButton: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendVerification = async (email: string) => {
    setIsSendingVerification(true);

    try {
      const res = await fetch(`${API_BASE_URL}/user/resend-verification/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) throw new Error("Failed to resend verification email");

      const data = await res.json();

      await MySwal.fire({
        icon: "success",
        title: "Verification Email Sent",
        text: data.message || "Verification email has been sent successfully.",
        showConfirmButton: true,
      });
    } catch (err) {
      console.error(err);
      await MySwal.fire({
        icon: "error",
        title: "Failed to Send Verification Email",
        text: "Could not send verification email. Please try again.",
        showConfirmButton: true,
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  function formatDateTime(dateString: string | null): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const monthRaw = date.toLocaleDateString("en-US", { month: "long" });
    const month =
      monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1).toLowerCase();
    const year = date.getFullYear();
    let time = date
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/^0/, "");

    time = time.replace(/(am|pm)$/i, (match) => " " + match.toUpperCase());
    return `${day} ${month}, ${year} ${time}`;
  }

  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailCheckError, setEmailCheckError] = useState("");
  const emailCheckTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const cleanupModal = (modalId: string) => {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    const cleanupBackdrop = () => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());

      document.body.classList.remove("modal-open");

      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      document.body.classList.remove("modal-open");

      const remainingBackdrops = document.querySelectorAll(".modal-backdrop");
      remainingBackdrops.forEach((backdrop) => {
        backdrop.remove();
      });
    };

    cleanupBackdrop();

    setTimeout(cleanupBackdrop, 100);
    setTimeout(cleanupBackdrop, 300);
  };

  const forceCleanupAllModals = () => {
    const allModals = document.querySelectorAll(".modal");
    allModals.forEach((modal) => {
      const modalInstance = Modal.getInstance(modal as HTMLElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    });

    document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());

    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    setTimeout(() => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const cleanupAddUserModal = () => {
    setShowAddUserForm(false);
    setEmailVerified(false);
    setUserExists(false);
    setFirstName("");
    setLastName("");
    setNewEmail("");
    setPassword("");
    setNewRole(null);
    setNewStatus("Active");
    setPasswordError("");
    setEmailCheckError("");
    setEmailCheckLoading(false);
    setShowNewPassword(false);

    const addUserModal = document.getElementById("add_user");
    if (addUserModal) {
      const modalInstance = Modal.getInstance(addUserModal);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    setTimeout(() => {
      document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }, 100);
  };

  const cleanupDeleteModal = () => {
    const deleteModal = document.getElementById("delete_translation");
    if (deleteModal) {
      const modalInstance = Modal.getInstance(deleteModal);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  };

  useEffect(() => {
    const resetAddUserForm = () => {
      setShowAddUserForm(false);
      setEmailVerified(false);
      setUserExists(false);
      setFirstName("");
      setLastName("");
      setNewEmail("");
      setPassword("");
      setNewRole(null);
      setNewStatus("Active");
      setPasswordError("");
      setEmailCheckError("");
      setEmailCheckLoading(false);
      setShowNewPassword(false);
    };

    const handleModalHidden = () => {
      setTimeout(() => {
        document.querySelectorAll(".modal-backdrop").forEach((e) => e.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";

        resetAddUserForm();
      }, 50);
    };

    const addUserModal = document.getElementById("add_user");
    const editUserModal = document.getElementById("edit_user");
    const deleteUserModal = document.getElementById("delete_translation");

    if (addUserModal) {
      addUserModal.addEventListener("show.bs.modal", () => {
        setIsModalOpening(true);
        resetAddUserForm();
      });
      addUserModal.addEventListener("shown.bs.modal", () => {
        setTimeout(() => {
          setIsModalOpening(false);
        }, 500);
      });
      addUserModal.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    if (editUserModal) {
      editUserModal.addEventListener("show.bs.modal", () => {
        setIsModalOpening(true);
      });
      editUserModal.addEventListener("shown.bs.modal", () => {
        setTimeout(() => {
          setIsModalOpening(false);
        }, 500);
      });
      editUserModal.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    if (deleteUserModal) {
      deleteUserModal.addEventListener("show.bs.modal", () => {
        setIsModalOpening(true);
      });
      deleteUserModal.addEventListener("shown.bs.modal", () => {
        setTimeout(() => {
          setIsModalOpening(false);
        }, 500);
      });
      deleteUserModal.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    return () => {
      if (addUserModal) {
        addUserModal.removeEventListener("show.bs.modal", resetAddUserForm);
        addUserModal.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
      if (editUserModal) {
        editUserModal.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
      if (deleteUserModal) {
        deleteUserModal.removeEventListener(
          "hidden.bs.modal",
          handleModalHidden
        );
      }
    };
  }, []);

  const shouldShowTab = (tabName: string) => {
    if (!userRole) return false;

    const roleKey = userRole.role_key;

    switch (tabName) {
      case "general":
        return [
          "CLIENTADMIN",
          "SUPERADMIN",
          "SALESUSER",
          "PLATFORM_ANALYST",
        ].includes(roleKey);
      case "organization":
        return roleKey === "CLIENTADMIN";
      case "lifecycle":
        return [
          "CLIENTADMIN",
          "SUPERADMIN",
          "SALESUSER",
          "PLATFORM_ANALYST",
        ].includes(roleKey);
      case "access":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST"].includes(
          roleKey
        );
      case "subscription":
        return ["CLIENTADMIN", "SUPERADMIN", "PLATFORM_ANALYST"].includes(
          roleKey
        );
      case "tenant":
        return roleKey === "SUPERADMIN" || roleKey === "PLATFORM_ANALYST";
      case "import":
        return roleKey === "CLIENTADMIN";
      case "integration":
        return roleKey === "CLIENTADMIN";
      default:
        return false;
    }
  };

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="access"
                    variant="bottom"
                    includeIcons={true}
                    context="access-subscription"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                {/* Sidebar */}
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link
                            to={route.userManagement}
                            className="fw-medium active"
                          >
                            User Management
                          </Link>
                          <Link to={route.teamManagement} className="fw-medium">
                            Team Management
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-9 col-lg-12">
                  {/* Custom Fields */}
                  <div className="card">
                    <div className="card-body">
                      {isLoading ? (
                        <UserManagementLoader />
                      ) : (
                        <>
                          {/* Search */}
                          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap">
                            <h4 className="fw-semibold mb-0">Users</h4>
                            {currentUserRole !== "PLATFORM_ANALYST" && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-toggle="modal"
                                data-bs-target="#add_user"
                                disabled={
                                  isStatsLoading || usedLicenses >= maxLicenses
                                }
                                title={
                                  isStatsLoading
                                    ? "Loading license data…"
                                    : usedLicenses >= maxLicenses
                                      ? "No more licenses available"
                                      : ""
                                }
                              >
                                <i className="ti ti-square-rounded-plus me-2" />
                                Add User
                              </button>
                            )}
                          </div>
                          {/* /Search */}
                          <div className="row m-0 p-0">
                            <div className="col-auto">
                              <div
                                className="card shadow-sm p-1"
                                style={{ height: "50px", minWidth: "140px" }}
                              >
                                <div className="d-flex align-items-start">
                                  <CreditCard
                                    size={20}
                                    className="me-2 text-primary "
                                  />
                                  <div className="text-center w-100">
                                    <small className="text-muted d-block">
                                      Allocated Licenses
                                    </small>
                                    <h5 className="mb-0">
                                      {isStatsLoading ? "…" : maxLicenses}
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-auto">
                              <div
                                className="card shadow-sm p-1"
                                style={{ height: "50px", minWidth: "140px" }}
                              >
                                <div className="d-flex align-items-start">
                                  <Users
                                    size={20}
                                    className="me-2 text-success "
                                  />
                                  <div className="text-center w-100">
                                    <small className="text-muted d-block">
                                      Used Licenses
                                    </small>
                                    <h5 className="mb-0">
                                      {isStatsLoading ? "…" : usedLicenses}
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Contact List */}
                          <div className="table-responsive custom-table">
                            <TableContainer>
                              <table
                                className="table dataTable no-footer"
                                id="language-list"
                              >
                                <thead className="thead-light">
                                  <tr>
                                    {/* <th>
                                      <div className="form-check form-check-md">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id="select-all-users"
                                          checked={!!selectAll}
                                          onChange={handleSelectAll}
                                        />
                                      </div>
                                    </th> */}
                                    <th>User Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Joining Date</th>{" "}
                                    {/* Added column header after Status */}
                                    {currentUserRole !== "PLATFORM_ANALYST" && (
                                      <th>Action</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {paginatedUsers.map((user, idx) => (
                                    <tr key={startIdx + idx}>
                                      {/* <td>
                                        <div className="form-check form-check-md">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`select-user-${user.tenantUserId}`}
                                            checked={
                                              !!(user.tenantUserId
                                                ? selectedItems.includes(
                                                    user.tenantUserId
                                                  )
                                                : false)
                                            }
                                            onChange={() =>
                                              user.tenantUserId &&
                                              handleSelectItem(
                                                user.tenantUserId
                                              )
                                            }
                                          />
                                        </div>
                                      </td> */}
                                      <td>
                                        <h2 className="table-avatar d-flex align-items-center">
                                          <Link
                                            to="#"
                                            className="profile-split"
                                            data-bs-toggle="modal"
                                            data-bs-target="#edit_user"
                                            onClick={() => openEditModal(user)}
                                          >
                                            {user.name}
                                          </Link>
                                        </h2>
                                      </td>
                                      <td>{user.email}</td>
                                      <td>{user.role}</td>
                                      <td>
                                        <span
                                          className={
                                            user.status === "Active"
                                              ? "badge badge-pill badge-status bg-success"
                                              : "badge badge-pill badge-status bg-danger"
                                          }
                                        >
                                          {user.status}
                                        </span>
                                      </td>
                                      <td>
                                        {formatDateTime(user.created_date)}
                                      </td>{" "}
                                      {/* Show created_date after Status */}
                                      {currentUserRole !==
                                        "PLATFORM_ANALYST" && (
                                          <td>
                                            <div className="dropdown table-action">
                                              <Link
                                                to="#"
                                                className="action-icon"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                data-bs-boundary="viewport"
                                                data-bs-popper-config='{"strategy":"fixed"}'
                                              >
                                                <i className="fa fa-ellipsis-v" />
                                              </Link>
                                              <div className="dropdown-menu dropdown-menu-end">
                                                <Link
                                                  className="dropdown-item"
                                                  to="#"
                                                  data-bs-toggle="modal"
                                                  data-bs-target="#edit_user"
                                                  onClick={() =>
                                                    openEditModal(user)
                                                  }
                                                >
                                                  <i className="ti ti-edit text-blue" />{" "}
                                                  Edit
                                                </Link>
                                                {user.status === "Inactive" && (
                                                  <Link
                                                    className="dropdown-item"
                                                    to="#"
                                                    onClick={() =>
                                                      handleResendVerification(
                                                        user.email
                                                      )
                                                    }
                                                  >
                                                    <i className="ti ti-mail text-warning" />{" "}
                                                    Send Account Verification
                                                    Email
                                                  </Link>
                                                )}
                                                <Link
                                                  className="dropdown-item"
                                                  to="#"
                                                  data-bs-toggle="modal"
                                                  data-bs-target="#delete_translation"
                                                  onClick={() => {
                                                    setEditTenantUserId(
                                                      user.tenantUserId
                                                    );
                                                    setEditUserId(user.userId);
                                                  }}
                                                >
                                                  <i className="ti ti-trash text-danger" />{" "}
                                                  Delete
                                                </Link>
                                              </div>
                                            </div>
                                          </td>
                                        )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableContainer>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <small>
                              Showing {startIdx + 1}–
                              {Math.min(startIdx + rowsPerPage, totalCount)} of{" "}
                              {totalCount}
                            </small>
                            <nav>
                              <ul className="pagination mb-0">
                                <li
                                  className={`page-item ${currentPage === 1 ? "disabled" : ""
                                    }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() =>
                                      setCurrentPage((p) => Math.max(1, p - 1))
                                    }
                                  >
                                    Prev
                                  </button>
                                </li>
                                {getPageNumbers().map((page) => (
                                  <li
                                    key={page}
                                    className={`page-item ${currentPage === page ? "active" : ""
                                      }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                ))}
                                <li
                                  className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                    }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() =>
                                      setCurrentPage((p) =>
                                        Math.min(totalPages, p + 1)
                                      )
                                    }
                                  >
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* /Custom Fields */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <>
        {/* Add user */}
        <div className="modal fade" id="add_user">
          <div className="modal-dialog modal-dialog-centered modal-xxl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add User</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {/* Email */}
                <div className="mb-3">
                  <label className="form-label">Email*</label>
                  <div className="d-flex">
                    <input
                      type="email"
                      className="form-control flex-grow-1"
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                      value={newEmail}
                      onChange={(e) => {
                        setNewEmail(e.target.value);
                        setUserExists(false);
                        setFirstName("");
                        setLastName("");
                        setShowAddUserForm(false);
                        setEmailCheckLoading(false);
                        setEmailCheckError("");
                        setEmailVerified(false);
                      }}
                      required
                      disabled={emailCheckLoading || isSavingNew}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: "none",
                      }}
                      onClick={async () => {
                        setEmailCheckLoading(true);
                        setEmailCheckError("");
                        try {
                          await checkUserExists(newEmail);
                          setShowAddUserForm(true);
                          setEmailVerified(true);
                        } catch {
                          setEmailCheckError("Error checking user");
                          setShowAddUserForm(false);
                          setEmailVerified(false);
                        } finally {
                          setEmailCheckLoading(false);
                        }
                      }}
                      disabled={!newEmail || emailCheckLoading || isSavingNew}
                    >
                      {emailCheckLoading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>
                  {/* Message under email field */}
                  {!emailVerified && !emailCheckLoading && !emailCheckError && (
                    <small className="text-muted">
                      Please verify if the user exists.
                    </small>
                  )}
                  {emailCheckError && (
                    <small className="text-danger">{emailCheckError}</small>
                  )}
                  {emailVerified && userExists && (
                    <small className="text-success">User exists.</small>
                  )}
                  {emailVerified && !userExists && (
                    <small className="text-warning">
                      User does not exist. Please fill in the details.
                    </small>
                  )}
                </div>
                {/* Only show rest of form if email verified */}
                {emailVerified && showAddUserForm && (
                  <>
                    {/* First Name */}
                    <div className="mb-3">
                      <label className="form-label">First Name*</label>
                      <input
                        type="text"
                        className="form-control  "
                        style={{
                          backgroundColor: userExists
                            ? "#dddddd"
                            : "transparent",
                        }}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        readOnly={userExists}
                      />
                    </div>
                    {/* Last Name */}
                    <div className="mb-3">
                      <label className="form-label">Last Name*</label>
                      <input
                        type="text"
                        className="form-control "
                        style={{
                          backgroundColor: userExists
                            ? "#dddddd"
                            : "transparent",
                        }}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        readOnly={userExists}
                      />
                    </div>
                    {/* Password (only if user does not exist) */}
                    {!userExists && (
                      <div className="mb-3">
                        <label className="form-label">Password*</label>
                        <div className="position-relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            className="form-control"
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              setPasswordError(
                                validatePassword(e.target.value)
                                  ? ""
                                  : "Password must be at least 8 characters, include 1 uppercase letter, 1 number, and 1 special character."
                              );
                            }}
                            required
                          />
                          <i
                            className={`ti ${showNewPassword ? "ti-eye-off" : "ti-eye"
                              } position-absolute top-50 end-0 translate-middle-y pe-3`}
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowNewPassword((v) => !v)}
                          />
                        </div>
                        {passwordError && (
                          <small className="text-danger">{passwordError}</small>
                        )}
                      </div>
                    )}
                    {/* Role */}
                    <div className="mb-3">
                      <label className="form-label">Role*</label>
                      <Select
                        options={roleOptions}
                        value={newRole}
                        onChange={(opt) => setNewRole(opt)}
                        placeholder="Select Role"
                      />
                    </div>
                    {/* Status */}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isSavingNew || !(emailVerified && showAddUserForm)}
                  style={{
                    backgroundColor: "#824eeb",
                    borderColor: "#824eeb",
                    color: "#fff",
                    opacity:
                      isSavingNew || !(emailVerified && showAddUserForm)
                        ? 0.6
                        : 1,
                  }}
                >
                  {isSavingNew ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Add user */}
        {/* Edit user */}
        <div className="modal fade" id="edit_user">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User</h5>
                <button
                  className="btn-close custom-btn-close border p-1 me-0 text-dark"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {/* First Name */}
                <div className="mb-3">
                  <label className="form-label">First Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    required
                  />
                </div>
                {/* Last Name */}
                <div className="mb-3">
                  <label className="form-label">Last Name*</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    required
                  />
                </div>
                {/* Email (read-only) */}
                <div className="mb-3">
                  <label className="form-label">Email*</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editEmail}
                    readOnly
                  />
                </div>
                {/* Password */}
                <div className="mb-3">
                  <label className="form-label">Password</label>
                  <div className="position-relative">
                    <input
                      type={showEditPassword ? "text" : "password"}
                      className="form-control"
                      value={editPassword}
                      onChange={(e) => {
                        setEditPassword(e.target.value);
                        setEditPasswordError(
                          e.target.value === "" ||
                            validatePassword(e.target.value)
                            ? ""
                            : "Must be 8+ chars, include uppercase, number, special char."
                        );
                      }}
                      placeholder="Leave blank to keep current password"
                    />
                    <i
                      className={`ti ${showEditPassword ? "ti-eye-off" : "ti-eye"
                        } position-absolute top-50 end-0 translate-middle-y pe-3`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowEditPassword((v) => !v)}
                    />
                  </div>
                  {editPasswordError && (
                    <small className="text-danger">{editPasswordError}</small>
                  )}
                </div>
                {/* Role */}
                <div className="mb-3">
                  <label className="form-label">Role*</label>
                  <Select
                    options={roleOptions}
                    value={editRole}
                    onChange={(opt) => setEditRole(opt)}
                    placeholder="Select Role"
                  />
                </div>
                {/* Status */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <div>
                    <label className="me-3">
                      <input
                        type="radio"
                        name="edit_status"
                        value="Active"
                        checked={!!(editStatus === "Active")}
                        onChange={() => setEditStatus("Active")}
                      />{" "}
                      Active
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="edit_status"
                        value="Inactive"
                        checked={!!(editStatus === "Inactive")}
                        onChange={() => setEditStatus("Inactive")}
                      />{" "}
                      Inactive
                    </label>
                  </div>
                </div>
              </div>
              {/* ─── And in your JSX, update the Save button to remove automatic dismissal: ─── */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleEditSave}
                  disabled={isSavingEdit}
                >
                  {isSavingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit user */}

        {/* Delete Translation */}
        <div className="modal fade" id="delete_translation" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remove User?</h4>
                  <p className="mb-0">Are you sure you want to remove it.</p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() =>
                        editTenantUserId !== null &&
                        handleDeleteUser(editTenantUserId)
                      }
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Yes, Delete it"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Translation */}
      </>
    </div>
  );
};

export default UserManagement;
